import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { useState, useEffect, useCallback, useRef } from 'react';
import Axios from '../../../config/axios';
import { Wheel } from 'react-custom-roulette';
import image from "../../../style/images/wheelpointer.png";
import { UrlConstant } from '../../../commom/UrlConstant';
import CelebrationPopup from './CelebrationPopup';
import { usePriceFormatter } from '../../../hooks';

interface SpinWheelProps {
  isDisabled: boolean;
  onSpinComplete: (earnedAmount: number, availableSpins: number) => void;
  fetchReferralData: () => void;
}

interface WheelSegment {
  option: string;
  style: { textColor: string };
}

const DOT_COUNT = 12;
const DOT_SIZE = 12;
const DISTANCE_FROM_CENTER = 150;
const ANGLE_STEP = (2 * Math.PI) / DOT_COUNT;

const WHEEL_COLORS = {
  background: ['#8540EB', '#FDBD1F'] as string[],
  text: ['#ffffff'] as string[],
  outerBorder: 'white',
  gradient: 'linear-gradient(#F5654D, #FFC80A)',
  innerBorder: '#25245E',
};

const DEFAULT_SEGMENT: WheelSegment = {
  option: '$0',
  style: { textColor: 'white' },
};

const DecorativeDot = ({ index }: { index: number }) => {
  const angle = ANGLE_STEP * index;
  return (
    <Box
      sx={{
        position: 'absolute',
        width: DOT_SIZE,
        height: DOT_SIZE,
        backgroundColor: 'gold',
        borderRadius: '50%',
        top: `calc(50% - ${DOT_SIZE / 2}px + ${Math.sin(angle) * DISTANCE_FROM_CENTER}px)`,
        left: `calc(50% - ${DOT_SIZE / 2}px + ${Math.cos(angle) * DISTANCE_FROM_CENTER}px)`,
        zIndex: 99,
        pointerEvents: 'none',
      }}
    />
  );
};

const SpinWheel = ({ isDisabled, onSpinComplete, fetchReferralData }: SpinWheelProps) => {
  const formatter = usePriceFormatter();

  const [mustSpin, setMustSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [segments, setSegments] = useState<WheelSegment[]>([DEFAULT_SEGMENT]);
  const [isSpinLoading, setIsSpinLoading] = useState(false);
  const pendingResultRef = useRef<{ rewardAmount: number; availableSpins: number } | null>(null);

  const loadIdleSegments = useCallback(async () => {
    try {
      const response = await Axios.get(UrlConstant.ReferFriend.getSpinDataV2);
      if (response.data.status) {
        const amounts: number[] = response.data.data.spin_amounts;
        setSegments(amounts.map((amount) => ({ option: formatter.display(amount), style: { textColor: 'white' } })));
      }
    } catch (error) {
      console.error('[SpinWheel] Failed to fetch idle segments:', error);
    }
  }, [formatter]);

  useEffect(() => {
    loadIdleSegments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSpinClick = useCallback(async () => {
    if (isDisabled || mustSpin || isSpinLoading) return;

    setIsSpinLoading(true); // dims the wheel and shows the overlay spinner
    try {
      const response = await Axios.post(UrlConstant.ReferFriend.redeemSpin);
      if (response.data.status) {
        const { rewardAmount, availableSpins, spin_amounts, spin_result_index } = response.data.data;
        setSegments(spin_amounts.map((amount: number) => ({ option: formatter.display(amount), style: { textColor: 'white' } })));
        pendingResultRef.current = { rewardAmount, availableSpins };
        setPrizeNumber(spin_result_index);
        setMustSpin(true); // wheel only starts moving once the correct target is known — single clean spin
      } else {
        console.error('[SpinWheel] Spin redeem failed:', response.data.message);
      }
    } catch (error) {
      console.error('[SpinWheel] Failed to redeem spin:', error);
    } finally {
      setIsSpinLoading(false);
    }
  }, [isDisabled, mustSpin, isSpinLoading, formatter]);

  const handleStopSpinning = useCallback(() => {
    setMustSpin(false);
    setPopupOpen(true);
    const result = pendingResultRef.current;
    if (result) {
      onSpinComplete(result.rewardAmount, result.availableSpins);
    }
  }, [onSpinComplete]);

  const handlePopupClose = useCallback(() => {
    setPopupOpen(false);
    pendingResultRef.current = null;
  }, []);

  const prizeAmount = pendingResultRef.current?.rewardAmount ?? 0;
  const isButtonDisabled = isDisabled || mustSpin || isSpinLoading;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'fit-content',
      }}
    >
      <Box
        sx={{
          margin: '46px 0',
          border: '0px solid transparent',
          padding: '8px',
          borderRadius: '100%',
          background: WHEEL_COLORS.gradient,
          backgroundSize: 'contain',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundClip: 'border-box',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            border: `24px solid ${WHEEL_COLORS.innerBorder}`,
            borderRadius: '50%',
            opacity: isSpinLoading ? 0.35 : 1,
            filter: isSpinLoading ? 'blur(1px)' : 'none',
            transition: 'opacity 0.25s ease, filter 0.25s ease',
          }}
        >
          {Array.from({ length: DOT_COUNT }, (_, i) => (
            <DecorativeDot key={i} index={i} />
          ))}
          <Box
            sx={{
              height: '272px',
              width: '272px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: 'scale(0.65) rotate(-45deg)',
            }}
          >
            <Wheel
              mustStartSpinning={mustSpin}
              prizeNumber={prizeNumber}
              startingOptionIndex={0}
              data={segments}
              backgroundColors={WHEEL_COLORS.background}
              textColors={WHEEL_COLORS.text}
              outerBorderColor={WHEEL_COLORS.outerBorder}
              outerBorderWidth={2}
              radiusLineWidth={0}
              disableInitialAnimation
              pointerProps={{
                src: image,
                style: {
                  rotate: '42deg',
                  position: 'absolute',
                  top: '50%',
                  left: '42%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'transparent',
                },
              }}
              onStopSpinning={handleStopSpinning}
            />
          </Box>
        </Box>

        {isSpinLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              pointerEvents: 'none',
            }}
          >
            <CircularProgress size={36} sx={{ color: '#fff' }} thickness={4} />
            <Typography fontSize={12} fontWeight={700} color="#fff" sx={{ textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
              Preparing…
            </Typography>
          </Box>
        )}
      </Box>

      <Button
        className="btn btn-primary"
        sx={{ py: '13px', height: 'fit-content' }}
        fullWidth
        onClick={handleSpinClick}
        disabled={isButtonDisabled}
        aria-label={mustSpin ? 'Spinning…' : 'Spin the wheel'}
      >
        {isSpinLoading ? 'Preparing…' : mustSpin ? 'Spinning…' : 'Spin now'}
      </Button>

      <CelebrationPopup
        open={isPopupOpen}
        onClose={handlePopupClose}
        prizeAmount={prizeAmount}
      />
    </Box>
  );
};

export default SpinWheel;